import DecimalType from "decimal.js";
import type { TCustomerEventLogMetadata } from "@./customer-event-log-writer";

import {
  CustomerEventLogSource,
  DiscountApplication,
  DiscountCode,
  SubscriptionItem,
  SubscriptionItemType,
} from "@/primary-models";

export interface IAddOnetimeRequest {
  /** The subscription to which the onetime should be added.
   * If the subscription does not exist, an error will be thrown. */
  subscriptionId: number;
  /** The onetime product to be added to the subscription.
   * If the product doesn't exist, it will be created.
   * Else, it will be updated. */
  product: SubscriptionItemType;
  // No option to provide a variant, since the variant of fresh always
  // matches the parent subscription and all other products don't have variants
  /** The quantity of product to add to the subscription.
   * If not set, defaults to {@link DEFAULT_ONETIME_QUANTITY}.
   * Must be greater than 0.
   * If the product doesn't exist, then a product with this quantity will be created.
   * After it is charged, the product's quantity will revert to 0.
   * If the product already exists, then the product's quantity will be increased by this amount.
   * After it is charged, the product's quantity will revert to its original amount.
   * If the product's quantity is modified before it is charged, then the reversion won't happen. */
  quantity?: number | null;
  /** If set, then the discount amount to use for the onetime.
   * If not set, then the discount amount will be calculated based
   * on the product's price so that the product becomes free.
   * If the product's price is 0 or the discountAmountOverride is 0,
   * then no discount will be created. */
  discountAmountOverride?: number | DecimalType | null;
  /** A note written by whoever added the onetime, to be attached to the created discount.
   * Usually describes why the onetime was added.
   * A suffix is added to the note that describes who the discount is for.
   * Even if this is unset, the suffix will still be written. */
  note?: string | null;
  /** A unique identifier for the reason for adding the onetime.
   * If not provided, {@link DEFAULT_ONETIME_CREDIT_REASON} will be used. */
  creditReason?: string | null;
}

export interface IAddOnetimeRequestInternal extends IAddOnetimeRequest {
  /** The user to whom the subscription belongs.
   * If the subscription does not belong to the user, an error will be thrown. */
  userId: string;
  /** Usually the ID of the admin adding the onetime.
   * Undefined if the system is the one adding the onetime
   * (e.g. due to some action triggered by a webhook) */
  causeUserId?: string;
  /** Usually ADMIN, unless the onetime is being added by some other process (e.g. SMS) */
  source: CustomerEventLogSource;
  /** Metadata to be written to the customer event log. */
  metadata?: TCustomerEventLogMetadata;
  /** This can be used to override the discount subcategory */
  discountSubcategory?: number;
  /** This can be used to override the post-charge quantity.
   * Note that null will set the post-charge quantity to null.
   * Set this to undefined if you want the service to calculate
   * the post-charge quantity. */
  postChargeQuantityOverride?: number | null;
}

export const DEFAULT_ONETIME_QUANTITY = 1 as const;
export const MAX_FRESH_RECIPE_ONETIME_QUANTITY = 8 as const;
export const MAX_TOTE_BAG_ONETIME_QUANTITY = 1 as const;
export const DEFAULT_ONETIME_CREDIT_REASON = "ONETIME";

// The types of onetimes that can be added to a subscription through the Admin-authed API.
// For now, we only allow CX to add these products to a subscription.
// However, the onetime system supports all products (except delivery fee)
// so this can easily be expanded in the future if needed.
export const ACCEPTABLE_ONETIMES = [
  SubscriptionItemType.NIBBLES_CHICKEN,
  SubscriptionItemType.NIBBLES_SALMON,
  SubscriptionItemType.NIBBLES_CHICKEN_POUCH,
  SubscriptionItemType.NIBBLES_SALMON_POUCH,
  SubscriptionItemType.NIBBLES_TUNA_POUCH,
  SubscriptionItemType.SPRINKLES,
] as const;

export type TAcceptableOnetime = typeof ACCEPTABLE_ONETIMES[number];

const IS_ACCEPTABLE_ONETIME: Record<TAcceptableOnetime, true>
  & Record<Exclude<SubscriptionItemType, TAcceptableOnetime>, false> = {
    [SubscriptionItemType.CLUCK]: false,
    [SubscriptionItemType.GOBBLE]: false,
    [SubscriptionItemType.MOOO]: false,
    [SubscriptionItemType.OINK]: false,
    [SubscriptionItemType.QUACK]: false,
    [SubscriptionItemType.SPLASH]: false,
    [SubscriptionItemType.BAAA]: false,
    [SubscriptionItemType.SWISH]: false,
    [SubscriptionItemType.SMOOTH_PECK]: false,
    [SubscriptionItemType.SMOOTH_CLUCK]: false,
    [SubscriptionItemType.SCOOP_PLANET]: false,
    [SubscriptionItemType.SCOOP_HEALTH]: false,
    [SubscriptionItemType.NIBBLES_SALMON]: true,
    [SubscriptionItemType.NIBBLES_CHICKEN]: true,
    [SubscriptionItemType.NIBBLES_SALMON_POUCH]: true,
    [SubscriptionItemType.NIBBLES_CHICKEN_POUCH]: true,
    [SubscriptionItemType.NIBBLES_TUNA_POUCH]: true,
    [SubscriptionItemType.SPRINKLES]: true,
    [SubscriptionItemType.TOTE_BAG]: false,
    [SubscriptionItemType.SHREDDED_CLUCK]: false,
    [SubscriptionItemType.SHREDDED_GOBBLE]: false,
  };

export function isAcceptableOnetime(item: string): item is TAcceptableOnetime {
  return !!(IS_ACCEPTABLE_ONETIME as Partial<Record<string, boolean>>)[item];
}

export type IAddOnetimeResponse = {
  item: SubscriptionItem;
  discountApplication?: DiscountApplication & { discount: DiscountCode };
};
